import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Download, GripVertical, Save } from "lucide-react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const DIMENSIONS = [
  { id: "source", label: "Source" },
  { id: "stage", label: "Stage" },
  { id: "city", label: "City" },
  { id: "rep", label: "Rep" },
];

const METRIC_OPTIONS = [
  { id: "count", label: "Leads" },
  { id: "converted", label: "Converted" },
  { id: "pipeline_value", label: "Pipeline ₹" },
  { id: "conversion_rate", label: "Rate %" },
];

function SortableMetric({ id, label }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className="crm-report-designer__metric">
      <button type="button" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </button>
      {label}
    </div>
  );
}

const CrmReportDesigner = ({ days = 30 }) => {
  const [dimension, setDimension] = useState("source");
  const [metrics, setMetrics] = useState(METRIC_OPTIONS.map((m) => m.id));
  const [report, setReport] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [loading, setLoading] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const sensors = useSensors(useSensor(PointerSensor));

  const loadReport = () => {
    setLoading(true);
    fetch(
      `${url}/leads/reports/build?dimension=${dimension}&days=${days}&metrics=${metrics.join(",")}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then(setReport)
      .finally(() => setLoading(false));
  };

  useEffect(loadReport, [dimension, days, metrics, url, token]);

  useEffect(() => {
    fetch(`${url}/leads/reports/presets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => setPresets(res?.presets || []))
      .catch(() => {});
  }, [url, token]);

  const onMetricDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = metrics.indexOf(active.id);
    const newIndex = metrics.indexOf(over.id);
    setMetrics(arrayMove(metrics, oldIndex, newIndex));
  };

  const savePreset = async () => {
    if (!presetName.trim()) return;
    const next = [
      ...presets.filter((p) => p.name !== presetName.trim()),
      { name: presetName.trim(), dimension, metrics, days },
    ];
    const res = await fetch(`${url}/leads/reports/presets`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ presets: next }),
    });
    if (res.ok) {
      const data = await res.json();
      setPresets(data.presets || next);
      toast.success("Report preset saved");
    }
  };

  const applyPreset = (p) => {
    setDimension(p.dimension);
    setMetrics(p.metrics || metrics);
    setPresetName(p.name);
  };

  const downloadCsv = async () => {
    const res = await fetch(
      `${url}/leads/reports/build?dimension=${dimension}&days=${days}&metrics=${metrics.join(",")}&format=csv`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `crm-${dimension}-${days}d.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const metricLabel = (id) => METRIC_OPTIONS.find((m) => m.id === id)?.label || id;

  return (
    <div className="crm-report-designer">
      <div className="crm-integrations__head">
        <strong>Report designer</strong>
        <button type="button" onClick={downloadCsv}>
          <Download size={14} /> Export
        </button>
      </div>

      <p className="crm-insights__muted">Drag metrics to reorder columns. Save presets for management reports.</p>

      <div className="crm-report-builder__dims">
        {DIMENSIONS.map((d) => (
          <button
            key={d.id}
            type="button"
            className={dimension === d.id ? "active" : ""}
            onClick={() => setDimension(d.id)}
          >
            By {d.label}
          </button>
        ))}
      </div>

      <p className="crm-insights__muted">Columns (drag to reorder)</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onMetricDragEnd}>
        <SortableContext items={metrics} strategy={horizontalListSortingStrategy}>
          <div className="crm-report-designer__metrics">
            {metrics.map((id) => (
              <SortableMetric key={id} id={id} label={metricLabel(id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="crm-report-designer__preset">
        <input
          placeholder="Preset name"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
        />
        <button type="button" onClick={savePreset}>
          <Save size={14} /> Save preset
        </button>
      </div>

      {presets.length > 0 && (
        <div className="crm-report-designer__preset-list">
          {presets.map((p) => (
            <button key={p.name} type="button" onClick={() => applyPreset(p)}>
              {p.name}
            </button>
          ))}
        </div>
      )}

      {loading && <p className="crm-insights__muted">Building…</p>}

      {report?.rows?.length > 0 && (
        <table className="crm-attribution__table">
          <thead>
            <tr>
              <th>{report.dimensionLabel}</th>
              {metrics.map((m) => (
                <th key={m}>{metricLabel(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                {metrics.map((m) => (
                  <td key={m}>
                    {m === "pipeline_value"
                      ? `₹${row[m]?.toLocaleString("en-IN")}`
                      : m === "conversion_rate"
                        ? `${row[m]}%`
                        : row[m]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CrmReportDesigner;
